import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Image, message, Popover, Table, Tabs, Tooltip } from 'antd';
import { useRequest, useSetState } from 'ahooks';
import { ColumnsType } from 'antd/es/table';
import cx from 'classnames';

import {
  ERunStatus,
  ESentiment,
  ILinkInfo,
  IRunRecordInfo,
  ISingleTaskResult,
  RUN_STATUS_MAP,
  runStatusOptions,
  SENTIMENT_CONFIG,
  TaskStatisticsVo,
} from '@/constants/geo';
import LongText from '@/components/LongText';

import styles from './index.module.less';
import { observer } from 'mobx-react';
import { postRunRecordDetail } from '@/axios/geo';

const PAGE_SIZE = 10;
const RUN_STATUS_COLOR_MAP: Partial<Record<ERunStatus, string>> = {
  [ERunStatus.Success]: '#52c41a',
  [ERunStatus.Fail]: '#ff4d4f',
  [ERunStatus.Waiting]: '#faad14',
  [ERunStatus.Running]: '#1677ff',
};

/** 运行记录结果展示 */
const RunRecordResult = ({
  record,
  reportData,
}: {
  record: IRunRecordInfo;
  reportData?: TaskStatisticsVo['analysisResult'];
}) => {
  const [info, setInfo] = useSetState<any>({
    platform: '',
    pageIndex: 1,
    pageSize: PAGE_SIZE,
    visibility: [],
    sentiment: [],
    status: [],
    // wordId、question、createTime排序，
    orderItems: [
      {
        field: 'question',
        type: 'DESC' as 'ASC' | 'DESC',
      },
    ],
  });
  const [expand, setExpand] = useState<boolean>(false);
  // 曝光度
  const aiVisibility = useCallback(
    (platform: string) => {
      const visible = reportData?.aiVisibilityPlatformData?.[platform]?.visible || 0;
      const total = reportData?.aiVisibilityPlatformData?.[platform]?.total || 1;
      return parseFloat(((visible / total) * 100).toFixed(2));
    },
    [reportData]
  );
  // 搜索词数量
  const searchKeywordsCount = useCallback(
    (platform: string) => {
      const visible = reportData?.aiVisibilityPlatformData?.[platform]?.visible || 0;
      return visible;
    },
    [reportData]
  );

  const { data, loading, runAsync } = useRequest(
    () => {
      if (!info.platform) {
        return Promise.resolve(null);
      }
      const { visibility, ...rest } = info;
      return postRunRecordDetail({
        ...rest,
        visibility: visibility.length > 1 ? undefined : visibility?.[0],
        id: record.id,
      });
    },
    {
      refreshDeps: [info, record.id],
    }
  );

  const platforms = useMemo(() => {
    return record.platformMappings.map((item) => item.platform);
  }, []);
  useEffect(() => {
    setInfo({
      platform: platforms[0] || '',
    });
  }, [platforms]);
  const renderLinkList = (list: ILinkInfo[]) => {
    return (
      <div className={styles['rrr_link_list']}>
        <div className={styles['rrr_link_list_title']}>{list.length} 个链接</div>
        {list.map((item, index) => {
          return (
            <div key={index} className={styles['rrr_link_list_item']}>
              <span>{index + 1}</span>
              <div className={styles['rrr_link_list_item_content']}>
                <div>网站：{item.siteName}</div>
                <div>
                  标题：
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                </div>
                <div>内容：{item.content}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTable = () => {
    const questionList = data?.records;

    const columns: ColumnsType<ISingleTaskResult> = [
      {
        title: '问题',
        dataIndex: 'question',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        defaultSortOrder: 'ascend',
        width: 200,
        render: (value, record) => {
          const questionNode = (
            <a
              onClick={() => {
                if (record.shareUrl) {
                  window.open(record.shareUrl, '_blank');
                  return;
                }
                navigator.clipboard.writeText(value || '');
              }}
            >
              {expand ? value : <LongText text={value || ''} />}
            </a>
          );

          return record.errorMsg ? (
            <Tooltip title={record.errorMsg}>
              {questionNode}
            </Tooltip>
          ) : questionNode;
        },
      },
      {
        title: '回答',
        dataIndex: 'answer',
        width: 300,
        render: (value) => {
          return (
            <div
              onClick={() => {
                navigator.clipboard.writeText(value || '');
              }}
            >
              {expand ? value : <LongText text={value || ''} />}
            </div>
          );
        },
      },
      {
        title: '相关链接',
        dataIndex: 'linkInfoList',
        width: 80,
        render: (value) => {
          return value?.length ? (
            <div>
              <Popover className={cx(styles['mr_10'])} trigger={['click']} content={renderLinkList(value)}>
                <Button type="link">查看</Button>
              </Popover>
              <Button
                type="link"
                onClick={() => {
                  const text = value.map((item: ILinkInfo) => `"${item.url}"`).join(',');
                  navigator.clipboard.writeText(`[${text}]`);
                  message.success('已复制到剪切板');
                }}
              >
                复制
              </Button>
            </div>
          ) : null;
        },
      },
      {
        title: '截图',
        dataIndex: 'screenSnapshotUrl',
        width: 80,
        render: (value) => {
          const isImg = /\.(jpeg|jpg|gif|png)$/.test(value);
          return isImg ? (
            <Image width={100} src={value} />
          ) : (
            <Button type="link" href={value} target="_blank" rel="noopener noreferrer">
              查看
            </Button>
          );
        },
      },
      {
        title: '运行状态',
        dataIndex: 'status',
        width: 90,
        render: (value, record) => {
          const statusText = value ? RUN_STATUS_MAP[value as ERunStatus] : '';
          const statusNode = (
            <span
              style={{
                color: RUN_STATUS_COLOR_MAP[value as ERunStatus],
              }}
            >
              {statusText}
            </span>
          );

          return record.errorMsg ? <Tooltip title={record.errorMsg}>{statusNode}</Tooltip> : statusNode;
        },
        filters: runStatusOptions.map((item) => ({
          text: item.label,
          value: item.value,
        })),
      },
      {
        title: 'AI可见性',
        dataIndex: 'visibility',
        width: 90,
        filters: [
          {
            text: '可见',
            value: true,
          },
          {
            text: '不可见',
            value: false,
          },
        ],
        render: (value, record) => {
          const companyNameExist = record?.aiResult?.companyNameExist;
          return typeof companyNameExist === 'boolean' ? (
            <div
              style={{
                color: companyNameExist ? '#FA8846' : '#5D8AF2',
              }}
            >
              {companyNameExist ? '可见' : '不可见'}
            </div>
          ) : null;
        },
      },
      {
        title: '推荐性',
        dataIndex: 'sentiment',
        width: 80,
        filters: [
          {
            text: '积极',
            value: 'positive',
          },
          {
            text: '中立',
            value: 'neutral',
          },
          {
            text: '消极',
            value: 'negative',
          },
        ],
        // filteredValue: info.sentiment.length > 0 ? info.sentiment : [],
        render: (value, record) => {
          const sentiment = record?.aiResult?.sentiment;
          const config = SENTIMENT_CONFIG[sentiment as ESentiment];

          return config ? (
            <div
              style={{
                color: config?.color,
              }}
            >
              {config?.name}
            </div>
          ) : null;
        },
      },
    ];

    const tableWidth = columns.reduce((prev, next) => {
      return prev + ((next.width as number) || 0);
    }, 0);

    return (
      <div>
        <Table
          loading={loading}
          columns={columns}
          dataSource={questionList || []}
          scroll={{
            x: tableWidth,
            y: 400,
          }}
          sticky
          pagination={{
            showSizeChanger: true,
            current: info.pageIndex,
            pageSize: info.pageSize,
            total: (data as any)?.total || 0,
          }}
          rowKey={(record) => record.wordId}
          onChange={(pagination, filters, sorter) => {
            console.log('filters', filters, sorter, pagination);
            setInfo({
              visibility: (filters.visibility as boolean[]) || [],
              sentiment: (filters.sentiment as string[]) || [],
              status: (filters.status as string[]) || [],
              pageIndex: pagination.current,
              pageSize: pagination.pageSize,
              orderItems: [
                {
                  field: (sorter as any)?.field || 'question',
                  type: (sorter as any)?.order === 'ascend' ? 'ASC' : 'DESC',
                },
              ],
            });
          }}
        />
      </div>
    );
  };

  return (
    <div>
      <div>
        <Checkbox checked={expand} onChange={(e) => setExpand(e.target.checked)}>
          展开
        </Checkbox>
      </div>
      <Tabs
        items={platforms.map((platform) => ({
          key: platform,
          label:
            (record.platformMappings.find((item) => item.platform === platform)?.name || platform) +
            `【${aiVisibility(platform)}%(${searchKeywordsCount(platform)}个)】`,
        }))}
        activeKey={info.platform}
        onChange={(key) => {
          setInfo({ platform: key, pageIndex: 1 });
        }}
      />
      {renderTable()}
    </div>
  );
};

export default observer(RunRecordResult);
